/**
 * HybridPDP — alternate PDP layout showing product in quick-view style
 * with category product grids below.
 *
 * Triggered by: ?hybrid=true query param OR product not orderable.
 * Used for: OOS products, email campaigns, merchandising.
 *
 * Layout:
 * - Top: compact product panel (quick view style — images + buy form)
 * - Below: up to 3 category mini-PLPs, each showing 8 tiles in a 4-col grid
 *
 * Category sources (sorted by position):
 * 1. Primary category (c_primaryCarousalPosition, default 1)
 * 2. Classification category (c_secondaryCarousalPosition, default 2)
 * 3. Custom category from c_hybridCustomCarousel "categoryId|limit" (c_customCarousalPosition, default 3)
 *
 * Reference: sfcc-website/org_hec/cartridge/templates/default/product/hybridProductDetails.isml
 */
import { Suspense } from "react";
import { Await, Link } from "react-router";

import { Skeleton } from "~/components/ui/skeleton";
import { useURL } from "~/contexts";
import { PlpProductTile } from "~/hec/components/product/plp-product-tile";
import { useHeadingFontClass } from "~/hec/hooks/use-heading-font-class";
import type { ProductHitWithSwatches } from "~/lib/app.types";
import { cn } from "~/lib/ui";

import type { HybridGrid } from "../utils/hybrid-grid-configs";
import { ProductImagesSkeleton } from "./product-images";

/**
 * Skeleton for the top product panel of a hybrid PDP — compact gallery
 * + buy form column. Only the top panel is reserved; the category grids
 * below own their own internal loading state (see `HybridCategoryGridSkeleton`),
 * so the skeleton would just duplicate that work.
 *
 * Only mount this skeleton when `?hybrid=true` is in the URL — for the
 * typical non-hybrid PDP, the hybrid Suspense resolves to nothing and a
 * skeleton would just flash and disappear.
 */
export const HybridPDPSkeleton = () => (
  <div className="hybrid-pdp mt-4" aria-label="Hybrid PDP loading">
    <div className="flex flex-col gap-6 md:flex-row md:gap-8">
      <div className="w-full md:w-5/12">
        <ProductImagesSkeleton compact />
      </div>
      <div className="flex w-full flex-col gap-3 md:w-7/12">
        <Skeleton className="h-7 w-4/5" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-5 w-32" />
        <Skeleton className="mt-1 h-8 w-28" />
        <Skeleton className="mt-2 h-11 w-full" />
      </div>
    </div>
  </div>
);

function HybridCategoryGridSkeleton({ limit }: { limit: number }) {
  return (
    <div className="hybrid-pdp mt-8">
      <Skeleton className="mb-4 h-8 w-48 lg:h-10" />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: limit }).map((_, i) => (
          <Skeleton key={i} className="h-[400px] rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function HybridCategoryGrid({ grid }: { grid: HybridGrid }) {
  const url = useURL();
  const headingFont = useHeadingFontClass();

  if (grid.products.length === 0) return null;
  const title = grid.displayName || grid.categoryId;

  return (
    <div className="hybrid-pdp mt-8">
      <h2
        className={cn(
          "mb-4 text-[26px] font-medium leading-[1.2]",
          headingFont,
        )}
      >
        <Link
          to={url(`/${grid.categoryId}`)}
          className="hover:underline"
          prefetch="intent"
        >
          {title}
        </Link>
      </h2>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        {grid.products.map((product, i) => {
          // Build a minimal ProductHitWithSwatches shape for PlpProductTile
          const hit: ProductHitWithSwatches = {
            productId: product.id,
            productName: product.name,
            price: product.price,
            image: product.imageUrl
              ? { link: product.imageUrl, alt: product.name }
              : undefined,
            orderable: product.orderable,
            c_productInfo: {
              id: product.id,
              name: product.name,
              c_imageGroups: [],
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any;

          return (
            <PlpProductTile
              key={`hybrid-${grid.categoryId}-${product.id}-${i}`}
              product={hit}
              className="w-full p-0"
              // SFCC-12141: select_item item_list_id/item_list_name — previously
              // blank for hybrid-PDP category grid clicks.
              gtmListId={grid.categoryId}
              gtmListName={title}
              gtmIndex={i + 1}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * HybridCategoryGrids — renders the category grids below the product panel.
 * Server-resolved via `loaderData.hybridGrids` (CIO browse + SCAPI getCategory
 * for display name fired in parallel per grid; see `product-detail-loader.ts`).
 *
 * The skeleton fallback assumes up to 3 grids — actual rendered count is
 * determined post-resolve. Empty `hybridGrids` resolves to null (no DOM).
 */
export function HybridCategoryGrids({
  hybridGrids,
}: {
  hybridGrids: Promise<HybridGrid[]>;
}) {
  return (
    <Suspense
      fallback={
        <div className="hybrid-pdp mt-6">
          {[1, 2, 3].map(i => (
            <HybridCategoryGridSkeleton key={i} limit={8} />
          ))}
        </div>
      }
    >
      <Await resolve={hybridGrids}>
        {grids => {
          if (!grids || grids.length === 0) return null;
          return (
            <div className="hybrid-pdp mt-6">
              {grids.map(grid => (
                <HybridCategoryGrid key={grid.categoryId} grid={grid} />
              ))}
            </div>
          );
        }}
      </Await>
    </Suspense>
  );
}
